'use client';

import { Text, Flex, Divider, Box } from '@chakra-ui/react';
import numeral from 'numeral';
import { useGetProtocolDataQuery } from '~/apollo/generated/graphql-codegen-generated';
import { useChainId } from 'wagmi';
import { BaseNetworkConfig } from '~/lib/config/base-config';
import { useTranslations } from 'next-intl';
import { useInternationalCurrency } from '~/i18n/hooks/useInternationalCurrency';
import { useInternationalNumber } from '~/i18n/hooks/useInternationalNumber';
import { useAnalytics } from '../../../../../analytics/context/AnalyticsContext';
function StatBox({
  headerLabel,
  children
}) {
  const t = useTranslations();
  return <Flex flexDirection="column" my="2" data-sentry-element="Flex" data-sentry-component="StatBox" data-sentry-source-file="ProtocolVolume.tsx">
      <Box borderBottomWidth="0px" borderColor="darkborder" p="4px" data-sentry-element="Box" data-sentry-source-file="ProtocolVolume.tsx">
        <Flex justifyContent="center" mb="5px" data-sentry-element="Flex" data-sentry-source-file="ProtocolVolume.tsx">
          <Text fontWeight={500} fontSize={{
          base: '14px',
          lg: '16px'
        }} textDecoration="" data-sentry-element="Text" data-sentry-source-file="ProtocolVolume.tsx">
            {t(headerLabel)}
          </Text>
        </Flex>
        {children}
      </Box>
    </Flex>;
}
interface StatLineProps {
  label: string;
  value: string;
}
function StatLine({
  label,
  value
}: StatLineProps) {
  const t = useTranslations();
  const formatCurrency = useInternationalCurrency();
  const cleanValue = value?.replace(/[$,]/g, '') || '0';
  const numericValue = parseFloat(cleanValue);
  return <Flex justifyContent="space-between" data-sentry-element="Flex" data-sentry-component="StatLine" data-sentry-source-file="ProtocolVolume.tsx">
      <Text fontSize={{
      base: 'sm',
      lg: '14px'
    }} data-sentry-element="Text" data-sentry-source-file="ProtocolVolume.tsx">{t(label)}</Text>
      <Text fontSize={{
      base: 'sm',
      lg: '14px'
    }} data-sentry-element="Text" data-sentry-source-file="ProtocolVolume.tsx">
        {' '}
        {formatCurrency(numericValue, 'USD', {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0
      })}
      </Text>
    </Flex>;
}
export function ProtocolVolume() {
  const chainId = useChainId();
  const t = useTranslations();
  const formatCurrency = useInternationalCurrency();
  const {
    data: apolloData
  } = useGetProtocolDataQuery({
    variables: {
      chainId
    },
    fetchPolicy: 'cache-first',
    skip: chainId === 8453
  });
  const {
    protocolData: baseData
  } = useAnalytics();
  const formatValue = (value: string) => {
    const cleanValue = value?.replace(/[$,]/g, '') || '0';
    return formatCurrency(parseFloat(cleanValue), 'USD', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    });
  };
  if (chainId === 8453) {
    return <>
        <Flex justifyContent="space-between" mt="2">
          <Text color="gray.300" fontSize={{
          base: 'sm',
          lg: '14px'
        }}>
            {t('V2')}
          </Text>
          <Text fontSize={{
          base: 'sm',
          lg: '14px'
        }}>
            {' '}
            {formatCurrency(baseData?.current_24hr_volume_v2 ?? 0, 'USD', {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0
          })}
          </Text>
        </Flex>

        <Divider pt="2" />

        <Flex justifyContent="space-between">
          <Text color="gray.300" fontSize={{
          base: 'sm',
          lg: '14px'
        }}>
            {t('V3')}
          </Text>
          <Text fontSize={{
          base: 'sm',
          lg: '14px'
        }}>
            {' '}
            {formatCurrency(baseData?.current_24hr_volume_v3 ?? 0, 'USD', {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0
          })}
          </Text>
        </Flex>

        <Divider pt="2" />

        <Flex justifyContent="space-between">
          <Text color="gray.300" fontSize={{
          base: 'sm',
          lg: '14px'
        }}>
            {t('All Time')}
          </Text>
          <Text fontSize={{
          base: 'sm',
          lg: '14px'
        }}>
            {' '}
            {formatCurrency((baseData?.cumulative_volume_v2 ?? 0) + (baseData?.cumulative_volume_v3 ?? 0), 'USD', {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0
          })}
          </Text>
        </Flex>
      </>;
  }
  const protocolData = apolloData?.protocolData;
  return <>
      <Flex justifyContent="space-between" mt="2" data-sentry-element="Flex" data-sentry-source-file="ProtocolVolume.tsx">
        <Text color="gray.300" fontSize={{
        base: 'sm',
        lg: '14px'
      }} data-sentry-element="Text" data-sentry-source-file="ProtocolVolume.tsx">
          {t('V2')}
        </Text>
        <Text fontSize={{
        base: 'sm',
        lg: '14px'
      }} data-sentry-element="Text" data-sentry-source-file="ProtocolVolume.tsx">
          {' '}
          {formatValue(protocolData?.v2TotalVolume)}
        </Text>
      </Flex>

      <Divider pt="2" data-sentry-element="Divider" data-sentry-source-file="ProtocolVolume.tsx" />

      <Flex justifyContent="space-between" data-sentry-element="Flex" data-sentry-source-file="ProtocolVolume.tsx">
        <Text color="gray.300" fontSize={{
        base: 'sm',
        lg: '14px'
      }} data-sentry-element="Text" data-sentry-source-file="ProtocolVolume.tsx">
          {t('V3')}
        </Text>
        <Text fontSize={{
        base: 'sm',
        lg: '14px'
      }} data-sentry-element="Text" data-sentry-source-file="ProtocolVolume.tsx">
          {' '}
          {formatValue(protocolData?.v3TotalVolume)}
        </Text>
      </Flex>

      <Divider pt="2" data-sentry-element="Divider" data-sentry-source-file="ProtocolVolume.tsx" />

      <Flex justifyContent="space-between" data-sentry-element="Flex" data-sentry-source-file="ProtocolVolume.tsx">
        <Text color="gray.300" fontSize={{
        base: 'sm',
        lg: '14px'
      }} data-sentry-element="Text" data-sentry-source-file="ProtocolVolume.tsx">
          {t('All Time')}
        </Text>
        <Text fontSize={{
        base: 'sm',
        lg: '14px'
      }} data-sentry-element="Text" data-sentry-source-file="ProtocolVolume.tsx">
          {' '}
          {formatValue(protocolData?.totalSwapVolume)}
        </Text>
      </Flex>
    </>;
}